// Component: Analytics Dashboard cho Virtual Advisor Q&A System
// Purpose: Monitor accuracy improvements với charts và metrics
// Note: Requires 'recharts' package - install with: npm install recharts

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  Spin,
  message,
  Table,
  Progress,
  Empty,
  Tag,
  Space,
  Typography
} from 'antd';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import qaPairService from '../../../services/qaPairService';

const { Title } = Typography;
const { Option } = Select;

interface MetricsData {
  successRateTrend: Array<{
    date: string;
    successRate: number;
    totalUsage: number;
    helpfulCount: number;
  }>;
  overallMetrics: {
    totalQAPairs: number;
    approvedPairs: number;
    totalUsage: number;
    avgSuccessRate: number;
    recentUsageCount: number;
  };
  feedbackDistribution: {
    helpful: number;
    notHelpful: number;
    noFeedback: number;
    total: number;
  };
  dateRange: {
    startDate: string;
    endDate: string;
    days: number;
  };
}

interface TopPair {
  pairId: number;
  question: string;
  answer: string;
  useCount: number;
  successCount: number;
  successRate: number;
  reviewStatus: string;
  recentUsageCount: number;
  avgSimilarity: number;
  createdAt: Date;
}

const COLORS = {
  helpful: '#52c41a',
  notHelpful: '#ff4d4f',
  noFeedback: '#d9d9d9',
  primary: '#1890ff',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f'
};

const AnalyticsDashboard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [timeRange, setTimeRange] = useState<number>(30);
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [topPairs, setTopPairs] = useState<TopPair[]>([]);

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);

      // Load metrics và top pairs song song
      const [metricsResponse, topPairsResponse] = await Promise.all([
        qaPairService.getAccuracyMetrics(timeRange),
        qaPairService.getTopQAPairs(10)
      ]);

      if (metricsResponse.success && metricsResponse.data) {
        setMetrics(metricsResponse.data);
      }

      if (topPairsResponse.success && topPairsResponse.data) {
        setTopPairs(topPairsResponse.data);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      message.error('Không thể tải dữ liệu analytics');
    } finally {
      setLoading(false);
    }
  };

  const renderMetricCards = () => {
    if (!metrics) return null;

    const { overallMetrics, feedbackDistribution } = metrics;
    const feedbackRate = feedbackDistribution.total > 0
      ? ((feedbackDistribution.helpful + feedbackDistribution.notHelpful) / feedbackDistribution.total * 100).toFixed(1)
      : '0';

    return (
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng số Q&A Pairs"
              value={overallMetrics.totalQAPairs}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: COLORS.primary }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              {overallMetrics.approvedPairs} đã phê duyệt
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tỷ lệ thành công trung bình"
              value={overallMetrics.avgSuccessRate}
              suffix="%"
              precision={2}
              valueStyle={{
                color: overallMetrics.avgSuccessRate >= 85 ? COLORS.success : COLORS.warning
              }}
              prefix={
                overallMetrics.avgSuccessRate >= 85 ? (
                  <ArrowUpOutlined />
                ) : (
                  <ArrowDownOutlined />
                )
              }
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              Mục tiêu: 85%
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng lượt sử dụng"
              value={overallMetrics.totalUsage}
              valueStyle={{ color: COLORS.primary }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              {overallMetrics.recentUsageCount} trong {timeRange} ngày qua
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tỷ lệ feedback"
              value={feedbackRate}
              suffix="%"
              valueStyle={{ color: COLORS.success }}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: '#8c8c8c' }}>
              {feedbackDistribution.helpful + feedbackDistribution.notHelpful} / {feedbackDistribution.total}
            </div>
          </Card>
        </Col>
      </Row>
    );
  };

  const renderSuccessRateTrend = () => {
    if (!metrics || metrics.successRateTrend.length === 0) {
      return <Empty description="Chưa có dữ liệu xu hướng" />;
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={metrics.successRateTrend}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tickFormatter={(value: string) => {
              const date = new Date(value);
              return `${date.getDate()}/${date.getMonth() + 1}`;
            }}
          />
          <YAxis
            domain={[0, 100]}
            label={{ value: 'Tỷ lệ thành công (%)', angle: -90, position: 'insideLeft' }}
          />
          <Tooltip
            formatter={(value: any, name: string) => {
              if (name === 'successRate') return [`${value.toFixed(2)}%`, 'Tỷ lệ thành công'];
              if (name === 'totalUsage') return [value, 'Lượt sử dụng'];
              return [value, name];
            }}
            labelFormatter={(label: string) => `Ngày: ${label}`}
          />
          <Legend />
          <Line
            type="monotone"
            dataKey="successRate"
            stroke={COLORS.primary}
            strokeWidth={2}
            name="Tỷ lệ thành công"
            dot={{ fill: COLORS.primary }}
          />
          <Line
            type="monotone"
            dataKey="totalUsage"
            stroke={COLORS.success}
            strokeWidth={2}
            name="Lượt sử dụng"
            dot={{ fill: COLORS.success }}
          />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  const renderFeedbackDistribution = () => {
    if (!metrics) return null;

    const { feedbackDistribution } = metrics;
    const pieData = [
      { name: 'Hữu ích', value: feedbackDistribution.helpful, color: COLORS.helpful },
      { name: 'Không hữu ích', value: feedbackDistribution.notHelpful, color: COLORS.notHelpful },
      { name: 'Chưa đánh giá', value: feedbackDistribution.noFeedback, color: COLORS.noFeedback }
    ];

    return (
      <Row gutter={16}>
        <Col span={12}>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry: any) => 
                  `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`
                }
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </Col>
        <Col span={12}>
          <Space direction="vertical" style={{ width: '100%', marginTop: 50 }}>
            <div>
              <CheckCircleOutlined style={{ color: COLORS.helpful, marginRight: 8 }} />
              <strong>Hữu ích:</strong> {feedbackDistribution.helpful} ({((feedbackDistribution.helpful / feedbackDistribution.total) * 100).toFixed(1)}%)
            </div>
            <div>
              <CloseCircleOutlined style={{ color: COLORS.notHelpful, marginRight: 8 }} />
              <strong>Không hữu ích:</strong> {feedbackDistribution.notHelpful} ({((feedbackDistribution.notHelpful / feedbackDistribution.total) * 100).toFixed(1)}%)
            </div>
            <div>
              <QuestionCircleOutlined style={{ color: COLORS.noFeedback, marginRight: 8 }} />
              <strong>Chưa đánh giá:</strong> {feedbackDistribution.noFeedback} ({((feedbackDistribution.noFeedback / feedbackDistribution.total) * 100).toFixed(1)}%)
            </div>
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}>
              <strong>Tổng:</strong> {feedbackDistribution.total} lượt sử dụng
            </div>
          </Space>
        </Col>
      </Row>
    );
  };

  const renderTopPairsTable = () => {
    const columns = [
      {
        title: 'Xếp hạng',
        dataIndex: 'index',
        key: 'index',
        width: 80,
        render: (_: any, __: any, index: number) => (
          <Tag color={index < 3 ? 'gold' : 'default'}>#{index + 1}</Tag>
        )
      },
      {
        title: 'Câu hỏi',
        dataIndex: 'question',
        key: 'question',
        ellipsis: true,
        width: 300
      },
      {
        title: 'Lượt dùng',
        dataIndex: 'useCount',
        key: 'useCount',
        width: 100,
        sorter: (a: TopPair, b: TopPair) => a.useCount - b.useCount,
        render: (count: number) => <strong>{count}</strong>
      },
      {
        title: 'Tỷ lệ thành công',
        dataIndex: 'successRate',
        key: 'successRate',
        width: 180,
        sorter: (a: TopPair, b: TopPair) => a.successRate - b.successRate,
        render: (rate: number) => (
          <div>
            <Progress
              percent={rate}
              size="small"
              status={rate >= 85 ? 'success' : rate >= 70 ? 'normal' : 'exception'}
              format={(percent) => `${percent?.toFixed(1)}%`}
            />
          </div>
        )
      },
      {
        title: 'Độ tương đồng TB',
        dataIndex: 'avgSimilarity',
        key: 'avgSimilarity',
        width: 130,
        render: (similarity: number) => (
          <Tag color={similarity >= 0.8 ? 'green' : 'orange'}>
            {(similarity * 100).toFixed(1)}%
          </Tag>
        )
      }
    ];

    return (
      <Table
        dataSource={topPairs}
        columns={columns}
        rowKey="pairId"
        pagination={false}
        size="small"
      />
    );
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3}>📊 Analytics Dashboard</Title>
        <Select
          value={timeRange}
          onChange={setTimeRange}
          style={{ width: 200 }}
          disabled={loading}
        >
          <Option value={7}>7 ngày qua</Option>
          <Option value={30}>30 ngày qua</Option>
          <Option value={90}>90 ngày qua</Option>
          <Option value={365}>1 năm qua</Option>
        </Select>
      </div>

      <Spin spinning={loading}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Metric Cards */}
          {renderMetricCards()}

          {/* Success Rate Trend */}
          <Card title="📈 Xu hướng tỷ lệ thành công">
            {renderSuccessRateTrend()}
          </Card>

          {/* Feedback Distribution */}
          <Card title="📊 Phân bố Feedback">
            {renderFeedbackDistribution()}
          </Card>

          {/* Top Q&A Pairs */}
          <Card title="🏆 Top 10 Q&A Pairs được sử dụng nhiều nhất">
            {renderTopPairsTable()}
          </Card>
        </Space>
      </Spin>
    </div>
  );
};

export default AnalyticsDashboard;
